import { useEffect, useRef, useState, type ReactNode } from 'react';

import {
  eingabeGueltig,
  eingabeStarten,
  eingabeTaste,
  eingabeUebernehmen,
  freiePosition,
  mengeErhoehen,
  mengeVerringern,
  type MengenEingabe,
} from '../mengenfeld';
import {
  betragCentAusText,
  betragTaste,
  vorwahlBeschreibung,
  vorwahlTasteAusZeichen,
  type VorwahlTaste,
} from '../vorwahl';
import { STEUERSAETZE, type Position, type Steuersatz } from '../warenkorb';
import { TastaturKnopf, istTastaturEsc, type useBildschirmtastatur } from './Bildschirmtastatur';
import { Mengenfeld } from './Ziffernblock';

/**
 * Oberfläche der Vorwahl V1 (Figma Section `430:19849`, freigegeben 2026-11-04): nichtmodales
 * Panel rechts neben den Kacheln (Desktop/T3/Tablet) bzw. Leiste von unten (Smartphone ≤ 600 px,
 * nur CSS), Hinweis „Nächster Artikel: …“ über den Kacheln (Komponente `VW/Vorwahl-Hinweis`
 * `430:19851`), Fehlermeldung (`POS/Hinweis` Art Fehler) und der Betragseditor „Als Betrag
 * buchen“ (VW-5). Die Logik liegt vollständig in `vorwahl.ts`; die freie Zeile entsteht über
 * `freiePosition` (`mengenfeld.ts`), es gibt keine zweite Preis- oder Mengenrechnung.
 *
 * Bewusst KEIN Dialog: keine Fokusfalle, kein `aria-modal` (Codex-Auflage „getrennte Zustände“).
 */

/** Tasten der Vorwahl: wie `ZIFFERN_TASTEN`, aber „,“ statt „00“ (O3, eigene Variante). */
export const VORWAHL_TASTEN: readonly VorwahlTaste[] = [
  '7',
  '8',
  '9',
  '4',
  '5',
  '6',
  '1',
  '2',
  '3',
  ',',
  '0',
  '⌫',
];

/** Meldung der Vorwahl (Titel fett, Text darunter). */
export interface VorwahlMeldungDaten {
  titel: string;
  text?: string;
}

/** Tastenfeld mit Komma (Klasse `ziffern-tasten` wie Ziffernblock und Gewichtsdialog). */
export function VorwahlZiffern(props: {
  onTaste: (t: VorwahlTaste) => void;
  beschriftung: string;
  gesperrt?: boolean;
  /** Fokus beim Antippen nicht verschieben (MB2-Direkteingabe: Enter übernimmt weiter). */
  fokusHalten?: boolean;
}) {
  return (
    <div className="ziffern-tasten vorwahl-ziffern" role="group" aria-label={props.beschriftung}>
      {VORWAHL_TASTEN.map((t) => (
        <button
          key={t}
          type="button"
          disabled={props.gesperrt === true}
          {...(props.fokusHalten === true
            ? {
                onMouseDown: (e: { preventDefault: () => void }) => {
                  e.preventDefault();
                },
              }
            : {})}
          onClick={() => {
            props.onTaste(t);
          }}
          aria-label={t === '⌫' ? 'Letzte Ziffer löschen' : t === ',' ? 'Komma' : t}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

function InfoSymbol() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" className="vorwahl-symbol">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 11v6M12 7.5v.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function WarnSymbol() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" className="vorwahl-symbol">
      <path
        d="M12 4l9 16H3zM12 10v4M12 17v.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Hinweis über den Kacheln (`VW/Vorwahl-Hinweis`): immer sichtbar, solange eine Vorwahl besteht –
 * auch bei ausgeblendetem Panel (O8: ersetzt den Zähler „3ד am Button). ✕ löscht.
 */
export function VorwahlHinweis(props: { text: string; onLoeschen: () => void }) {
  const b = vorwahlBeschreibung(props.text);
  if (b === null) {
    return null;
  }
  return (
    <div className="vorwahl-hinweis">
      <InfoSymbol />
      <span className="vorwahl-hinweis-text">
        Nächster Artikel: <strong>{b.kurz}</strong>
      </span>
      <span className="vorwahl-hinweis-geltung">gilt nur für den nächsten Artikel oder Scan</span>
      <button
        type="button"
        className="vorwahl-hinweis-x"
        aria-label="Vorwahl löschen"
        onClick={props.onLoeschen}
      >
        ✕
      </button>
    </div>
  );
}

/**
 * Fehlermeldung (`POS/Hinweis` Art Fehler, nicht blockierend). Die Live-Region steht immer im
 * DOM (auch leer), damit Hilfstechnik jede neue Meldung ansagt.
 */
export function VorwahlMeldung(props: { meldung: VorwahlMeldungDaten | null }) {
  const m = props.meldung;
  return (
    <div className={m === null ? 'vorwahl-meldung leer' : 'vorwahl-meldung'} role="alert">
      {m !== null && (
        <>
          <WarnSymbol />
          <div>
            <strong>{m.titel}</strong>
            {m.text !== undefined && <span>{m.text}</span>}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Vorwahl-Panel (VW-1, VW-7, VW-8) bzw. Leiste (VW-9, per CSS ≤ 600 px). Kein Dialog. Bei aktivem
 * Betragseditor zeigt es statt der Vorwahl den Editor (VW-5).
 */
export function VorwahlPanel(props: {
  id: string;
  text: string;
  /** Vorwahl ruht (Dialog, Zahlung, Sperre): Tasten gesperrt, Grund als Zeile. */
  ruhtGrund: string | null;
  meldung: VorwahlMeldungDaten | null;
  onTaste: (t: VorwahlTaste) => void;
  onLoeschen: () => void;
  onAlsBetrag: () => void;
  /** Smartphone „Fertig“: klappt die Leiste ein, die Vorwahl bleibt (O6). */
  onFertig: () => void;
  /** Betragseditor (aktiv) statt der Vorwahl. */
  betrag?: ReactNode;
}) {
  const b = vorwahlBeschreibung(props.text);
  const gesperrt = props.ruhtGrund !== null;
  if (props.betrag !== undefined && props.betrag !== null) {
    return (
      <aside
        className="vorwahl-panel karte mit-betrag"
        id={props.id}
        aria-label="Als Betrag buchen"
      >
        {props.betrag}
      </aside>
    );
  }
  return (
    <aside className="vorwahl-panel karte" id={props.id} aria-labelledby={`${props.id}-titel`}>
      <div className="vorwahl-kopf">
        <h2 id={`${props.id}-titel`}>Vorwahl</h2>
        <span className="vorwahl-meta">Menge oder Gewicht</span>
        <button type="button" className="taste zweit vorwahl-fertig" onClick={props.onFertig}>
          Fertig
        </button>
      </div>
      <div className="vorwahl-anzeige-zeile">
        <output
          className={`vorwahl-anzeige${props.text === '' ? ' leer' : ''}${props.text.length > 6 ? ' lang' : ''}`}
          aria-label="Vorwahl"
          aria-live="polite"
        >
          {props.text === '' ? '–' : props.text}
        </output>
        <button
          type="button"
          className="taste zweit vorwahl-c"
          aria-label="Vorwahl löschen (C)"
          disabled={props.text === ''}
          onClick={props.onLoeschen}
        >
          C
        </button>
      </div>
      <p className="vorwahl-einheit">
        {gesperrt
          ? `Vorwahl ruht – ${props.ruhtGrund ?? ''}`
          : (b?.einheit ?? 'Einheit nach Artikel: Stück oder kg')}
      </p>
      <VorwahlMeldung meldung={props.meldung} />
      <VorwahlZiffern onTaste={props.onTaste} beschriftung="Vorwahl eingeben" gesperrt={gesperrt} />
      <p className="vorwahl-regel">
        Gilt nur für den nächsten Artikel oder Scan, danach gelöscht. Stück: 1–999 · kg: bis 3
        Nachkommastellen (Komma oder Punkt).
      </p>
      <div className="vorwahl-abstand" aria-hidden="true" />
      <button
        type="button"
        className="taste zweit vorwahl-betrag"
        disabled={gesperrt}
        onClick={props.onAlsBetrag}
      >
        Als Betrag buchen
      </button>
      <p className="vorwahl-regel">Freie Zeile mit Steuersatz und Bezeichnung – ohne Artikel.</p>
    </aside>
  );
}

/** Taste der physischen Tastatur im Betragseditor (null = nicht zuständig). */
function editorTaste(key: string): VorwahlTaste | null {
  if (key === 'Backspace') {
    return '⌫';
  }
  if (key === 'Delete') {
    return 'C';
  }
  return vorwahlTasteAusZeichen(key);
}

/**
 * Betragseditor „Als Betrag buchen“ (VW-5) im Panel: Betrag in Euro mit Komma (O3), Steuersatz
 * (alle Sätze einschließlich 19 % und 4,9 %), Bezeichnung mit Bildschirmtastatur der
 * Verkaufsfläche und das MB2-Mengenfeld [−][Wert][+] mit Direkteingabe (O10, Regeln M3–M6 wie
 * bisher). Abbrechen/Zurück (Esc) bzw. Hinzufügen (Enter). Die Position entsteht nur über
 * `freiePosition`.
 */
export function BetragEditor(props: {
  /** Betragstext (Euro, Komma) – vom Verkauf gehalten (Tastatur-Umleitung). */
  text: string;
  setText: (text: string) => void;
  /** Meldung der Eingabeprüfung bzw. null. */
  onMeldung: (m: VorwahlMeldungDaten | null) => void;
  meldung: VorwahlMeldungDaten | null;
  /** Bildschirmtastatur der Verkaufsfläche (Ziel „bezeichnung“). */
  bt: ReturnType<typeof useBildschirmtastatur>;
  /** Position übernehmen; false = nicht übernommen (Editor bleibt offen). */
  onHinzufuegen: (p: Position) => boolean;
  onAbbrechen: () => void;
  /** Ziel der Tastatur-Umleitung (vom Verkauf aufgerufen, wenn der Fokus außerhalb liegt). */
  tastenZiel?: { current: ((t: VorwahlTaste) => void) | null };
  /**
   * Aktion erst nach der Entscheidung der Scanerkennung über gehaltene Tasten ausführen
   * (`ScanErkennung.danach`): früher getippte Ziffern zählen zuerst, ein laufender Scan wird nie
   * zerlegt (Codex-Runden 2–4 UI). Ohne Angabe sofort.
   */
  danach?: (aktion: () => void) => void;
}) {
  const { text, bt } = props;
  const [steuersatz, setSteuersatz] = useState<Steuersatz>('20');
  const [bezeichnung, setBezeichnung] = useState('');
  const [menge, setMenge] = useState(1);
  /** MB2: laufende Direkteingabe der Stückzahl (null = Ziffern tippen den Betrag). */
  const [mengenEingabe, setMengenEingabe] = useState<MengenEingabe | null>(null);
  const wertKnopf = useRef<HTMLButtonElement | null>(null);
  const anzeige = useRef<HTMLOutputElement | null>(null);
  const fertig = useRef(false);
  const betrag = betragCentAusText(text);
  const cent = 'cent' in betrag ? betrag.cent : 0;
  // Fokus in den Editor (Codex-Runde 1 UI): Enter und Esc wirken sofort, auch per Tastatur;
  // beim Schließen zurück auf „Als Betrag buchen“ im Panel (keine Fokusfalle, kein Dialog)
  useEffect(() => {
    anzeige.current?.focus();
    return () => {
      requestAnimationFrame(() => {
        // nur wenn der Fokus verloren ging – nie einem Dialog (Bar, Gutschein …) wegnehmen
        if (document.activeElement === null || document.activeElement === document.body) {
          document.querySelector<HTMLElement>('.vorwahl-panel button.vorwahl-betrag')?.focus();
        }
      });
    };
  }, []);

  // Aktueller Stand für Tasten und Abschlüsse vor dem nächsten Rendern (z. B. von der
  // Scanerkennung freigegebene Ziffer plus folgendes Komma bzw. „Hinzufügen“ im selben Ereignis)
  const textAktuell = useRef(text);
  textAktuell.current = text;
  const mengenAktuell = useRef(mengenEingabe);
  mengenAktuell.current = mengenEingabe;
  function setzeMengenEingabe(m: MengenEingabe | null): void {
    mengenAktuell.current = m;
    setMengenEingabe(m);
  }
  const mengeAktuell = useRef(menge);
  mengeAktuell.current = menge;
  function setzeMenge(m: number): void {
    mengeAktuell.current = m;
    setMenge(m);
  }
  /** Editor noch eingehängt (eine wartende Aktion nach dem Schließen verfällt). */
  const eingehaengt = useRef(true);
  useEffect(() => {
    eingehaengt.current = true;
    return () => {
      eingehaengt.current = false;
    };
  }, []);
  /** Touch- und Abschlussaktionen: erst nach der Entscheidung über gehaltene Tasten. */
  function spaeter(aktion: () => void): void {
    const ausfuehren = (): void => {
      // abgeschlossen (übernommen oder abgebrochen): weitere wartende Aktionen verfallen synchron
      if (eingehaengt.current && !fertig.current) {
        aktion();
      }
    };
    if (props.danach === undefined) {
      ausfuehren();
    } else {
      props.danach(ausfuehren);
    }
  }
  function abbrechen(): void {
    spaeter(() => {
      fertig.current = true;
      props.onAbbrechen();
    });
  }

  /** Ziffernblock- bzw. Tastaturtaste: während der Direkteingabe auf die Menge, sonst Betrag. */
  function taste(t: VorwahlTaste): void {
    const m = mengenAktuell.current;
    if (m !== null) {
      if (t !== ',') {
        setzeMengenEingabe(eingabeTaste(m, t));
      }
      return;
    }
    const r = betragTaste(textAktuell.current, t);
    textAktuell.current = r.text;
    props.setText(r.text);
    props.onMeldung(
      r.meldung === null ? null : { titel: r.meldung, text: 'Eingabe bleibt stehen.' },
    );
  }
  /**
   * Touch-Taste: nach gehaltenen Tastaturziffern (sie kamen früher) – sonst vertauschte sich die
   * Reihenfolge (Codex-Runde 3 UI). `taste` selbst wartet nicht, weil es auch die nachgereichten
   * Ereignisse verarbeitet.
   */
  function touchTaste(t: VorwahlTaste): void {
    spaeter(() => {
      taste(t);
    });
  }
  if (props.tastenZiel !== undefined) {
    props.tastenZiel.current = taste;
  }

  function eingabeOeffnen(): void {
    if (bt.tastatur !== null) {
      bt.schliessen('keiner');
    }
    spaeter(() => {
      setzeMengenEingabe(eingabeStarten(mengeAktuell.current));
      wertKnopf.current?.focus();
    });
  }

  function eingabeBeenden(uebernehmen: boolean): void {
    spaeter(() => {
      const m = mengenAktuell.current;
      if (m === null) {
        return;
      }
      if (uebernehmen) {
        if (!eingabeGueltig(m)) {
          return;
        }
        setzeMenge(eingabeUebernehmen(m));
      }
      setzeMengenEingabe(null);
    });
  }

  function schritt(f: (m: number) => number): void {
    spaeter(() => {
      const m = mengenAktuell.current;
      const basis = m !== null && eingabeGueltig(m) ? eingabeUebernehmen(m) : mengeAktuell.current;
      setzeMengenEingabe(null);
      setzeMenge(f(basis));
    });
  }

  function hinzufuegen(): void {
    spaeter(() => {
      if (fertig.current) {
        return;
      }
      const r = betragCentAusText(textAktuell.current);
      if ('meldung' in r) {
        props.onMeldung({ titel: r.meldung });
        return;
      }
      const position = freiePosition({
        cent: r.cent,
        menge: mengeAktuell.current,
        steuersatz,
        bezeichnung,
      });
      if (position === null) {
        return;
      }
      fertig.current = true;
      if (!props.onHinzufuegen(position)) {
        fertig.current = false;
      }
    });
  }

  function bestaetigen(): void {
    spaeter(() => {
      if (mengenAktuell.current !== null) {
        eingabeBeenden(true);
        return;
      }
      hinzufuegen();
    });
  }

  return (
    <div
      className="betrag-editor"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.altKey || e.ctrlKey || e.metaKey) {
          return;
        }
        const imFeld = e.target instanceof HTMLInputElement;
        if (e.key === 'Escape') {
          // R5: erstes Esc bei offener Bildschirmtastatur schließt nur sie
          if (istTastaturEsc(e)) {
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          if (mengenAktuell.current !== null) {
            eingabeBeenden(false);
          } else {
            abbrechen();
          }
          return;
        }
        if (e.key === 'Enter') {
          if (e.target instanceof HTMLButtonElement && e.target !== wertKnopf.current) {
            return; // Enter auf einer Taste löst nur diese Taste aus
          }
          e.preventDefault();
          e.stopPropagation();
          bestaetigen();
          return;
        }
        if (imFeld) {
          return; // Bezeichnung: Zeichen gehören dem Feld
        }
        const t = editorTaste(e.key);
        if (t !== null) {
          e.preventDefault();
          e.stopPropagation();
          taste(t);
        }
      }}
    >
      <div className="vorwahl-kopf">
        <h2>Als Betrag buchen</h2>
        <button type="button" className="taste zweit vorwahl-zurueck" onClick={abbrechen}>
          Zurück
        </button>
      </div>
      <div className="vorwahl-anzeige-zeile">
        <output
          ref={anzeige}
          tabIndex={-1}
          className={`vorwahl-anzeige betrag${text === '' ? ' leer' : ''}${text.length > 5 ? ' lang' : ''}${mengenEingabe === null ? '' : ' ruhend'}`}
          aria-label="Betrag"
          aria-live="polite"
          {...(mengenEingabe === null
            ? {}
            : {
                // M4: Antippen des Betrags kehrt ohne Mengenänderung zum Betrag zurück
                onClick: () => {
                  eingabeBeenden(false);
                  anzeige.current?.focus();
                },
              })}
        >
          {text === '' ? '0,00 €' : `${text} €`}
        </output>
        <button
          type="button"
          className="taste zweit vorwahl-c"
          aria-label="Betrag löschen (C)"
          disabled={text === ''}
          onClick={() => {
            touchTaste('C');
          }}
        >
          C
        </button>
      </div>
      <p className="vorwahl-einheit">
        {mengenEingabe !== null
          ? 'Betrag bleibt – der Ziffernblock tippt gerade die Menge'
          : 'Betrag der freien Zeile (aus der Vorwahl übernommen)'}
      </p>
      <VorwahlMeldung meldung={props.meldung} />
      <span className="dlg-feldname">Steuersatz</span>
      <div className="chips" role="group" aria-label="Steuersatz">
        {STEUERSAETZE.map((s) => (
          <button
            key={s.satz}
            type="button"
            className={s.satz === steuersatz ? 'aktiv' : ''}
            aria-pressed={s.satz === steuersatz}
            onClick={() => {
              setSteuersatz(s.satz);
            }}
          >
            {s.satz} %
          </button>
        ))}
      </div>
      <span className="dlg-feldname">Bezeichnung (sonst „Artikel“)</span>
      <div className="ziffern-bezeichnung">
        <input
          value={bezeichnung}
          onChange={(e) => {
            setBezeichnung(e.target.value);
          }}
          placeholder="Bezeichnung (sonst „Artikel“)"
          aria-label="Bezeichnung"
          {...bt.feld('bezeichnung', {
            name: 'Bezeichnung',
            setzen: setBezeichnung,
            nachSchliessen: () => {
              anzeige.current?.focus();
            },
          })}
        />
        <TastaturKnopf
          steuerung={bt.knopf('bezeichnung')}
          klein
          beschriftung="Tastatur für die Bezeichnung"
        />
      </div>
      <Mengenfeld
        menge={menge}
        eingabe={mengenEingabe}
        wertKnopf={wertKnopf}
        onVerringern={() => {
          schritt(mengeVerringern);
        }}
        onErhoehen={() => {
          schritt(mengeErhoehen);
        }}
        onWert={() => {
          if (mengenEingabe === null) {
            eingabeOeffnen();
          } else {
            eingabeBeenden(true);
          }
        }}
      />
      <VorwahlZiffern
        onTaste={touchTaste}
        beschriftung={mengenEingabe === null ? 'Betrag eingeben' : 'Menge eingeben'}
        fokusHalten={mengenEingabe !== null}
      />
      <div className="dlg-fuss vorwahl-fuss">
        <button
          type="button"
          className="taste zweit"
          data-kuerzel="Esc"
          onClick={() => {
            // M4: während der Direkteingabe bricht „Abbrechen“ nur die Eingabe ab
            if (mengenAktuell.current !== null) {
              eingabeBeenden(false);
            } else {
              abbrechen();
            }
          }}
        >
          Abbrechen
        </button>
        {mengenEingabe !== null ? (
          <button
            type="button"
            className="taste gross"
            data-kuerzel="Enter"
            disabled={!eingabeGueltig(mengenEingabe)}
            onClick={() => {
              eingabeBeenden(true);
            }}
          >
            Übernehmen
          </button>
        ) : (
          <button
            type="button"
            className="taste gross"
            data-kuerzel="Enter"
            disabled={cent <= 0}
            onClick={hinzufuegen}
          >
            Hinzufügen
          </button>
        )}
      </div>
    </div>
  );
}
